import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination, Autoplay } from "swiper/modules";

import main1 from "../assets/main1.webp";
import main2 from "../assets/main2.webp";
import main3 from "../assets/mainteam1.webp";
import main4 from "../assets/mainteam2.webp";

import team1 from "../assets/team1.webp";
import team2 from "../assets/team2.webp";
import team3 from "../assets/team3.webp";
import team4 from "../assets/team4.webp";

import mikrotik from "../assets/mikrotik.png";
import cisco from "../assets/cisco.png";
import hikvision from "../assets/hikvision.png";
import l2x from "../assets/l2xlogo.png";

export default function Team() {
  const values = [
    { icon: "🤝", title: "Collaboration", desc: "Working together to achieve impactful results." },
    { icon: "🚀", title: "Innovation", desc: "Always pushing boundaries with creativity." },
    { icon: "💡", title: "Integrity", desc: "Honest, transparent, and ethical practices." },
    { icon: "🌍", title: "Global Impact", desc: "Solutions that go beyond borders." },
  ];

  const mainTeamPhotos = [main1, main2, main3, main4];
  const actionTeamPhotos = [team1, team2, team3, team4];
  const partners = [
    { logo: mikrotik, name: "Mikrotik" },
    { logo: cisco, name: "Cisco" },
    { logo: hikvision, name: "HikVision" },
    { logo: l2x, name: "L2X" },
  ];

  return (
    <section className="bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 transition-all duration-300">

      {/* ================= HERO SECTION ================= */}
      <div className="flex flex-col lg:flex-row items-center justify-between px-6 lg:px-20 py-20">
        
        {/* Carousel */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="w-full lg:w-1/2 mb-10 lg:mb-0"
        >
          <Swiper
            modules={[Pagination, Autoplay]}
            pagination={{ clickable: true }}
            autoplay={{ delay: 2500, disableOnInteraction: false }}
            spaceBetween={20}
            slidesPerView={1}
            className="rounded-2xl shadow-lg"
          >
            {mainTeamPhotos.map((photo, index) => (
              <SwiperSlide key={index}>
                <img
                  src={photo}
                  alt={`Main Team ${index + 1}`}
                  className="w-full h-96 object-cover rounded-2xl"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>

        {/* Text */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="lg:ml-12 text-center lg:text-left max-w-xl"
        >
          <h2 className="text-4xl font-bold text-blue-600 dark:text-blue-300 mb-4">
            Meet Our Team
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
            Our team is made of passionate professionals who believe in creating 
            innovative and sustainable solutions. Collaboration, creativity, and 
            integrity are at the heart of every success.
          </p>
          <p className="text-lg font-medium text-gray-900 dark:text-white italic">
            "Together, we don’t just work — we create a vision for the future."
          </p>
        </motion.div>

      </div>

      {/* ================= VALUES SECTION ================= */}
      <div className="bg-gray-50 dark:bg-gray-800 py-16 px-6 lg:px-20 text-center transition-colors duration-300">
        <h3 className="text-3xl font-bold text-blue-600 dark:text-blue-300 mb-10">
          Our Values
        </h3>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto">
          {values.map((value, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              className="
                bg-white dark:bg-white/10 dark:backdrop-blur-md 
                dark:border dark:border-white/10
                shadow-md rounded-xl p-6
              "
            >
              <div className="text-5xl mb-4">{value.icon}</div>
              <h4 className="text-xl font-semibold dark:text-white mb-2">
                {value.title}
              </h4>
              <p className="text-gray-600 dark:text-gray-300">
                {value.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ================= OUR TEAM IN ACTION ================= */}
      <div className="py-16 px-6 lg:px-20">
        <h3 className="text-3xl font-bold text-center text-blue-600 dark:text-blue-300 mb-10">
          Our Team in Action
        </h3>

        <Swiper
          modules={[Pagination, Autoplay]}
          pagination={{ clickable: true }}
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          spaceBetween={30}
          slidesPerView={1}
          breakpoints={{
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
          className="max-w-6xl mx-auto"
        >
          {actionTeamPhotos.map((photo, index) => (
            <SwiperSlide key={index}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="rounded-2xl overflow-hidden shadow-lg"
              >
                <img
                  src={photo}
                  alt={`Team in Action ${index + 1}`}
                  className="w-full h-80 object-cover"
                />
              </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* ================= PARTNERS ================= */}
      <div className="py-20 bg-white dark:bg-gray-900 transition-all duration-300">
        <h3 className="text-3xl font-bold text-center text-blue-600 dark:text-blue-300 mb-12">
          Our Partners
        </h3>

        <Swiper
          modules={[Autoplay]}
          spaceBetween={30}
          slidesPerView={3}
          loop={true}
          autoplay={{
            delay: 2500,
            disableOnInteraction: false,
          }}
          breakpoints={{
            640: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 5 },
          }}
          className="max-w-6xl mx-auto"
        >
          {partners.map((partner, index) => (
            <SwiperSlide key={index}>
              <div className="flex justify-center">
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="h-16 object-contain dark:brightness-200 transition"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

    </section>
  );
}
